import React, {
  useCallback, useContext, useEffect,
} from 'react';
import { showNotification } from '@mantine/notifications';
import {
  Box, Divider, Group, Menu, Text, UnstyledButton, useMantineTheme,
} from '@mantine/core';
import { IconChevronDown, IconLogout, IconUser } from '@tabler/icons';
import { Link } from 'react-router-dom';

import { AuthActionType, AuthContext } from '../contexts/auth';
import { AppContext } from '../contexts/app';
import { useNavigateRef } from '../hooks/use-navigate-ref';
import { handleNonFormErrors } from '../helpers/errors';
import { useApi } from '../hooks/use-api';
import { grp } from '../routes';
import { ToggleColorScheme } from './toggle-color-scheme-segmented';

export const ProfileMenu: React.FC = () => {
  const { appState } = useContext(AppContext);
  const { authState, authDispatch } = useContext(AuthContext);
  const { user } = authState;
  const navigate = useNavigateRef();
  const theme = useMantineTheme();

  const [userFetchCall] = useApi('users.me', { loadingInitialState: true });

  const loadUser = useCallback(async () => {
    try {
      const userData = await userFetchCall();
      if (userData.user) {
        authDispatch({
          type: AuthActionType.USER_SYNC,
          payload: {
            user: userData.user,
          },
        });
      }
    } catch (error) {
      handleNonFormErrors(error, (message) => {
        showNotification({ color: 'red', message });
      });
    }
  }, [authDispatch, userFetchCall]);

  const handleLogout = () => {
    navigate.current(grp('logout'));
  };

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return (
    <Menu
      shadow="md"
      width={250}
      position="bottom-end"
    >
      <Menu.Target>
        <UnstyledButton
          c={theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[6]}
          h="auto"
          py="xs"
          px="sm"
          fz="sm"
          fw={500}
          sx={(_theme) => ({
            border: `1px solid ${_theme.colorScheme === 'dark' ? _theme.colors.gray[7] : _theme.colors.gray[3]}`,
            borderRadius: _theme.radius.xl,
          })}
        >
          <Group noWrap position="apart" spacing="xs">
            <Text display={{ base: 'none', sm: 'block' }}>
              {appState.currentProjectName || '-'}
            </Text>
            <Divider orientation="vertical" display={{ base: 'none', sm: 'block' }} />
            <Box component={IconUser} size={18} display={{ base: 'block', sm: 'none' }} />
            <Text display={{ base: 'none', sm: 'block' }}>
              {
                user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email
              }
            </Text>
            <Box display={{ base: 'none', sm: 'block' }}>
              <IconChevronDown size={14} />
            </Box>
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Box p="sm" display={{ base: 'block', sm: 'none' }}>
          <Text fw={600} truncate>
            {appState.currentProjectName || '-'}
          </Text>
          <Text truncate>
            {
              user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email
            }
          </Text>
        </Box>
        <Menu.Divider mb={15} display={{ base: 'block', sm: 'none' }} />

        <Menu.Label>Appearance</Menu.Label>
        <Box>
          <ToggleColorScheme />
        </Box>

        <Menu.Divider my={15} />

        <Menu.Label>Account</Menu.Label>
        <Menu.Item
          component={Link}
          to={grp('account', { activeTab: 'general' })}
          icon={<IconUser size={14} />}
        >
          Account Settings
        </Menu.Item>
        <Menu.Item
          icon={<IconLogout size={14} color={theme.colors.red[7]} />}
          onClick={handleLogout}
        >
          Logout
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
